import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Chip, Skeleton, Container, Paper, Button, Grid, TextField, IconButton, Tooltip,
} from '@mui/material';
import { ArrowBack, PlayArrow, Search } from '@mui/icons-material';
import NavBar from '../../../Components/NavBar/navbar';
import ArcadeHubNav, { arcadeHubPath, ARCADE_SECTIONS } from '../ArcadeHubNav';
import { API_ENDPOINTS } from '../../../utils/config';
import { THEME } from '../../../theme';
import { buildGameNameMap, gameDisplayName, resolveUserLabel, formatAgentLabel } from './displayHelpers';
import { EmptyState } from '../../../Components/Arcade/ui/StateViews';
import usePageTitle from '../../../Components/Arcade/ui/usePageTitle';

function StatBox({ label, value }) {
    return (
        <Box sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 2 }}>
            <Typography variant="caption" sx={{ color: THEME.secondary }}>{label}</Typography>
            <Typography variant="h6" sx={{ color: 'white' }}>{value}</Typography>
        </Box>
    );
}

function SubmissionsTable({ rows, loading, gameNameMap = {} }) {
    if (loading) {
        return (
            <Box sx={{ p: 2 }}>
                {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} variant="text" height={36} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
                ))}
            </Box>
        );
    }
    if (!rows.length) {
        return <EmptyState message="No submissions yet for this agent." />;
    }
    return (
        <TableContainer sx={{ maxHeight: 480 }}>
            <Table stickyHeader size="small">
                <TableHead>
                    <TableRow>
                        {['Game', 'Score', 'Verified', 'Date', 'Replay'].map((h) => (
                            <TableCell key={h} sx={{ bgcolor: THEME.cardBg, color: THEME.secondary, borderBottom: `1px solid ${THEME.border}` }}>{h}</TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, idx) => {
                        const runId = row.run_id || row._id || row.id;
                        const gameId = row.game_id || row.game;
                        return (
                            <TableRow key={runId || idx} hover>
                                <TableCell sx={{ color: 'white', borderBottom: `1px solid ${THEME.grid}` }}>
                                    <Link to={`/arcade/dashboard/games/${gameId}`} style={{ color: 'white', textDecoration: 'none' }}>
                                        {gameDisplayName(gameId, gameNameMap)}
                                    </Link>
                                </TableCell>
                                <TableCell sx={{ borderBottom: `1px solid ${THEME.grid}` }}>
                                    <Chip size="small" label={row.score ?? 0} sx={{ bgcolor: 'transparent', border: `1px solid ${THEME.primary}`, color: THEME.primary, fontWeight: 'bold' }} />
                                </TableCell>
                                <TableCell sx={{ borderBottom: `1px solid ${THEME.grid}` }}>
                                    {row.verified ? (
                                        <Chip size="small" label="Verified" sx={{ bgcolor: THEME.primary, color: 'black', fontWeight: 'bold' }} />
                                    ) : (
                                        <Chip size="small" label="Unverified" variant="outlined" sx={{ color: THEME.secondary, borderColor: THEME.border }} />
                                    )}
                                </TableCell>
                                <TableCell sx={{ color: THEME.secondary, borderBottom: `1px solid ${THEME.grid}` }}>
                                    {row.timestamp ? new Date(row.timestamp).toLocaleDateString() : '—'}
                                </TableCell>
                                <TableCell sx={{ borderBottom: `1px solid ${THEME.grid}` }}>
                                    {runId ? (
                                        <Tooltip title="Play">
                                            <IconButton
                                                size="small"
                                                component={Link}
                                                to={`/arcade/replay/${runId}`}
                                                aria-label="Play"
                                                sx={{ color: THEME.primary }}
                                            >
                                                <PlayArrow fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    ) : '—'}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

/**
 * Dual-purpose: embedded "Agents" tab in DashboardHome (props: leaderboard, loading)
 * or standalone routed page at /arcade/dashboard/agents/:agentId.
 */
const AgentDetailView = ({ games = [], leaderboard = [], loading: loadingProp = false }) => {
    const { agentId: routeAgentId } = useParams();
    usePageTitle(routeAgentId ? `Agent ${routeAgentId}` : 'Dashboard');
    const isStandalone = Boolean(routeAgentId);

    const [search, setSearch] = useState('');
    const [agent, setAgent] = useState(null);
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(loadingProp);
    const [error, setError] = useState(null);
    const [standaloneGames, setStandaloneGames] = useState([]);

    const gameNameMap = useMemo(
        () => buildGameNameMap(games.length ? games : standaloneGames),
        [games, standaloneGames]
    );

    useEffect(() => {
        if (!isStandalone) return;
        let mounted = true;
        async function fetchAgentData() {
            setLoading(true);
            setError(null);
            try {
                const [agentRes, subsRes, gamesRes] = await Promise.all([
                    fetch(API_ENDPOINTS.AGENT_DETAIL(routeAgentId)),
                    fetch(API_ENDPOINTS.AGENT_SUBMISSIONS(routeAgentId)),
                    fetch(API_ENDPOINTS.GAMES_LIST),
                ]);
                if (agentRes.ok) {
                    const a = await agentRes.json();
                    if (mounted) setAgent(a);
                }
                if (subsRes.ok) {
                    const s = await subsRes.json();
                    const list = s.submissions || (Array.isArray(s) ? s : []);
                    if (mounted) setSubmissions(list);
                } else if (mounted) {
                    setSubmissions([]);
                }
                if (gamesRes.ok) {
                    const g = await gamesRes.json();
                    if (mounted) setStandaloneGames(Array.isArray(g) ? g : (g.games || []));
                }
                if (!agentRes.ok && !subsRes.ok) {
                    throw new Error(`status ${agentRes.status}/${subsRes.status}`);
                }
            } catch (e) {
                console.error('Failed to load agent:', e);
                if (mounted) setError('Failed to load agent');
            }
            if (mounted) setLoading(false);
        }
        fetchAgentData();
        return () => { mounted = false; };
    }, [isStandalone, routeAgentId]);

    // Embedded aggregate view: distinct agents derived client-side from leaderboard
    const agentRows = useMemo(() => {
        const byAgent = {};
        (leaderboard || []).forEach((e) => {
            const key = e.agent_id;
            if (!key) return;
            if (!byAgent[key]) {
                byAgent[key] = {
                    agent_id: key,
                    label: formatAgentLabel(e),
                    username: resolveUserLabel(e),
                    runs: 0,
                    bestScore: -Infinity,
                    game_id: e.game_id || e.game,
                };
            }
            byAgent[key].runs += 1;
            const score = Number(e.score) || 0;
            if (score > byAgent[key].bestScore) byAgent[key].bestScore = score;
        });
        return Object.values(byAgent).filter((a) =>
            !search
            || a.agent_id?.toLowerCase().includes(search.toLowerCase())
            || a.label?.toLowerCase().includes(search.toLowerCase())
            || a.username?.toLowerCase().includes(search.toLowerCase())
        );
    }, [leaderboard, search]);

    if (!isStandalone) {
        return (
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 700 }}>Agents</Typography>
                    <TextField
                        size="small"
                        placeholder="Search agents..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{ startAdornment: <Search fontSize="small" sx={{ color: THEME.secondary, mr: 1 }} /> }}
                        sx={{ '& .MuiOutlinedInput-root': { color: 'white' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: THEME.border } }}
                    />
                </Box>
                {loadingProp ? (
                    <Box sx={{ p: 2 }}>
                        {[...Array(5)].map((_, i) => (
                            <Skeleton key={i} variant="text" height={36} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
                        ))}
                    </Box>
                ) : agentRows.length === 0 ? (
                    <EmptyState message="No agents found yet." />
                ) : (
                    <TableContainer sx={{ maxHeight: 480 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    {['Agent', 'Game', 'Runs', 'Best Score', ''].map((h) => (
                                        <TableCell key={h} sx={{ bgcolor: THEME.cardBg, color: THEME.secondary, borderBottom: `1px solid ${THEME.border}` }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {agentRows.map((a) => (
                                    <TableRow key={a.agent_id} hover>
                                        <TableCell sx={{ color: 'white', borderBottom: `1px solid ${THEME.grid}` }}>{a.label}</TableCell>
                                        <TableCell sx={{ color: THEME.secondary, borderBottom: `1px solid ${THEME.grid}` }}>
                                            {gameDisplayName(a.game_id, gameNameMap)}
                                        </TableCell>
                                        <TableCell sx={{ color: THEME.secondary, borderBottom: `1px solid ${THEME.grid}` }}>{a.runs}</TableCell>
                                        <TableCell sx={{ borderBottom: `1px solid ${THEME.grid}` }}>
                                            <Chip size="small" label={a.bestScore === -Infinity ? 0 : a.bestScore} sx={{ bgcolor: 'transparent', border: `1px solid ${THEME.primary}`, color: THEME.primary, fontWeight: 'bold' }} />
                                        </TableCell>
                                        <TableCell sx={{ borderBottom: `1px solid ${THEME.grid}` }}>
                                            <Button size="small" component={Link} to={`/arcade/dashboard/agents/${a.agent_id}`} sx={{ color: THEME.primary, textTransform: 'none' }}>
                                                View Agent
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
        );
    }

    return (
        <Box sx={{ bgcolor: THEME.bg, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <NavBar />
            <Box sx={{ flexGrow: 1, pt: 12, pb: 8 }}>
                <Container maxWidth="lg">
                    <ArcadeHubNav activeSection={ARCADE_SECTIONS.dashboard} />
                    <Button component={Link} to={arcadeHubPath(ARCADE_SECTIONS.dashboard)} startIcon={<ArrowBack />} sx={{ color: THEME.secondary, mb: 3 }}>
                        Back to Dashboard
                    </Button>
                    <Typography variant="h3" sx={{ color: 'white', fontWeight: 800, mb: 1 }}>
                        {formatAgentLabel({
                            agent_id: agent?.agent_id || routeAgentId,
                            username: agent?.username,
                            email: agent?.email,
                            user_id: agent?.user_id,
                        })}
                    </Typography>
                    <Typography variant="body2" sx={{ color: THEME.secondary, mb: 3 }}>Agent Detail</Typography>

                    {error && (
                        <Paper sx={{ p: 2, mb: 3, bgcolor: 'rgba(239, 71, 67, 0.08)', border: `1px solid ${THEME.accent}`, borderRadius: 2 }}>
                            <Typography variant="body2" sx={{ color: THEME.accent }}>
                                Some of this agent's details can't be shown right now. Please try again in a moment.
                            </Typography>
                        </Paper>
                    )}

                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={6} sm={3}>
                            <StatBox label="Owner" value={loading ? '…' : resolveUserLabel(agent || {})} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <StatBox label="Game" value={loading ? '…' : gameDisplayName(agent?.game_id || agent?.problem_id, gameNameMap)} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <StatBox label="Total Submissions" value={loading ? '…' : submissions.length} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <StatBox label="Best Score" value={loading ? '…' : (agent?.best_score ?? '—')} />
                        </Grid>
                    </Grid>

                    <Paper sx={{ bgcolor: THEME.cardBg, border: `1px solid ${THEME.border}`, borderRadius: 3 }}>
                        <Box sx={{ p: 2, borderBottom: `1px solid ${THEME.border}` }}>
                            <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>Submissions</Typography>
                        </Box>
                        <SubmissionsTable rows={submissions} loading={loading} gameNameMap={gameNameMap} />
                    </Paper>
                </Container>
            </Box>
        </Box>
    );
};

export default AgentDetailView;
